/**
 * Zeitanteilige Abrechnung. Tagegenau (BGH: taggenaue Abgrenzung bei Mieterwechsel).
 * Datumsangaben immer als ISO 'YYYY-MM-DD', inklusive Grenzen.
 */

export type ISODate = string;

const MS_TAG = 86_400_000;

function toUTC(d: ISODate): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  if (!m) throw new Error(`Ungültiges Datum: ${d} (erwartet YYYY-MM-DD)`);
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export interface Zeitraum {
  von: ISODate;
  bis: ISODate; // inklusive
}

/** Anzahl Tage im Zeitraum, beide Grenzen inklusive. */
export function tage(z: Zeitraum): number {
  const von = toUTC(z.von);
  const bis = toUTC(z.bis);
  if (bis < von) throw new Error(`Zeitraum endet vor Beginn: ${z.von} .. ${z.bis}`);
  return Math.round((bis - von) / MS_TAG) + 1;
}

/** Schnittmenge zweier Zeiträume, oder null wenn keine Überschneidung besteht. */
export function schnitt(a: Zeitraum, b: Zeitraum): Zeitraum | null {
  const von = toUTC(a.von) >= toUTC(b.von) ? a.von : b.von;
  const bis = toUTC(a.bis) <= toUTC(b.bis) ? a.bis : b.bis;
  return toUTC(von) <= toUTC(bis) ? { von, bis } : null;
}

/** Überschneidungstage eines Mietverhältnisses mit dem Abrechnungszeitraum. */
export function ueberschneidungTage(a: Zeitraum, b: Zeitraum): number {
  const s = schnitt(a, b);
  return s ? tage(s) : 0;
}

/**
 * Addiert Monate zu einem Datum. Läuft ein Tag über das Monatsende hinaus (31.01. + 1
 * Monat), wird auf den letzten Tag des Zielmonats gekappt (28./29.02.). Rein, UTC.
 * Grundlage der Fristberechnung im Anlagenbuch (nächste Prüfung = Basis + Intervall).
 */
export function addiereMonate(datum: ISODate, monate: number): ISODate {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datum);
  if (!m) throw new Error(`Ungültiges Datum: ${datum} (erwartet YYYY-MM-DD)`);
  const jahr = Number(m[1]);
  const monat0 = Number(m[2]) - 1;
  const tag = Number(m[3]);
  const ziel = monat0 + Math.trunc(monate);
  const zielJahr = jahr + Math.floor(ziel / 12);
  const zielMonat0 = ((ziel % 12) + 12) % 12;
  const letzterTag = new Date(Date.UTC(zielJahr, zielMonat0 + 1, 0)).getUTCDate();
  const zielTag = Math.min(tag, letzterTag);
  const d = new Date(Date.UTC(zielJahr, zielMonat0, zielTag));
  return d.toISOString().slice(0, 10);
}

/**
 * Anzahl angefangener/voller Monate zur Berechnung geleisteter Vorauszahlungen.
 * Vorauszahlungen werden monatlich geschuldet; ein angefangener Monat zählt voll,
 * sofern das Mietverhältnis an mindestens einem Tag des Monats bestand.
 */
export function monateBeruehrt(mv: Zeitraum, zeitraum: Zeitraum): number {
  const s = schnitt(mv, zeitraum);
  if (!s) return 0;
  const [vy, vm] = s.von.split('-').map(Number);
  const [by, bm] = s.bis.split('-').map(Number);
  return (by - vy) * 12 + (bm - vm) + 1;
}
